import {Component, Input, OnInit} from '@angular/core';
import {User} from "../../models/user.model";
import {AppStateService} from "../../providers/app-state.service";
import {ModalController} from "@ionic/angular";
import {VetDoctorVetVisitTypeService} from "../../providers/vet-doctor-vet-visit-type.service";
import {forkJoin, of, Subscription} from "rxjs";
import {VetClinic, VetDoctor} from "../../models/vet-clinic.model";
import {CommonUtils} from "../../utils/common";
import {VetineoVisitTypeBundle} from "../../models/vetineo-visit-type-bundle.model";
import {VetDoctorVetVisitType, VetineoVisitTypeMinutes} from "../../models/vet-doctor-vet-visit-type.model";
import {VetClinicService} from "../../providers/vet-clinic.service";
import {finalize, switchMap} from "rxjs/operators";

@Component({
  selector: 'app-select-multiple-doctors',
  templateUrl: './select-multiple-doctors.component.html',
  styleUrls: ['./select-multiple-doctors.component.scss'],
})
export class SelectMultipleDoctorsComponent implements OnInit {
  @Input() user: User;
  @Input() vetClinic: VetClinic;
  @Input() doctors: VetDoctor[];
  @Input() vetineoVisitTypeBundle: VetineoVisitTypeBundle;
  @Input() vetDoctorVisitTypes: VetDoctorVetVisitType[];

  public doctorsSelected: VetDoctor[] = [];
  isBusy: boolean;
  subscriptions: Subscription = new Subscription();
  doctorsAvailableToCheck: VetDoctor[];
  minutesDefined = Object.values(VetineoVisitTypeMinutes)?.filter(value => typeof value === "number") as number[];
  private vetDoctorsToSetDefaultMinutes: VetDoctor[] = [];


  constructor(private appStateService: AppStateService,
              private modalController: ModalController,
              private vetDoctorVetVisitTypeService: VetDoctorVetVisitTypeService,
              private vetClinicService: VetClinicService,
              protected commonUtils: CommonUtils) {
  }

  ngOnInit() {

    this.registerSubscription(this.appStateService.isBusySubject.subscribe(e => this.isBusy = e));
    this.doctorsAvailableToCheck = this.getNotAssignedDoctors().filter(doctor => doctor.defaultVisitMinutes);
  }

  registerSubscription(sub: Subscription) {
    if (sub) {
      this.subscriptions.add(sub);
    }
  }

  onClose() {
    this.vetDoctorsToSetDefaultMinutes.forEach(doctor => {
      doctor.defaultVisitMinutes = undefined;
    });
    this.modalController.dismiss();
  }

  getNotAssignedDoctors(): VetDoctor[] {
    return this.doctors?.filter(e => this.doctorNotInVetDoctorsVetVisitTypes(e));
  }

  onCheckboxChange($event, vetDoctor: VetDoctor) {
    if (typeof $event.detail.checked !== "boolean") {
      return;
    }
    if ($event.detail.checked) {
      this.doctorsSelected.push(vetDoctor);
    } else {
      this.doctorsSelected = this.doctorsSelected.filter(e => e.uuid !== vetDoctor.uuid);
    }
  }

  isDoctorSelected(vetDoctor: VetDoctor) {
  return this.doctorsSelected.find(doctor => doctor.uuid === vetDoctor.uuid);
  }

  private doctorNotInVetDoctorsVetVisitTypes(vetDoctor: VetDoctor) {
    if (!this.vetDoctorVisitTypes) {
      return true;
    }
    return !this.vetDoctorVisitTypes.filter(e => e.vetDoctorUUID === vetDoctor.uuid)[0];
  }

  addDoctors() {
    this.appStateService.updateIsBusySubject(true);
    const updateDoctorRequests$ = this.vetDoctorsToSetDefaultMinutes.map(doctor =>
      this.vetClinicService.updateVetDoctor(doctor)
    );
    const updateAllDoctors$ = updateDoctorRequests$.length > 0
      ? forkJoin(updateDoctorRequests$)
      : of([]);

    this.registerSubscription(
      updateAllDoctors$.pipe(
        switchMap(() => {
          let uuids = this.doctorsSelected.map(e => e.uuid);
          let bundleUUID = this.vetineoVisitTypeBundle.vetineoVisitTypeBundleUUID ? this.vetineoVisitTypeBundle.vetineoVisitTypeBundleUUID : this.vetineoVisitTypeBundle.uuid;
          return this.vetDoctorVetVisitTypeService.addDoctorsToBundle(bundleUUID, uuids, this.vetClinic.vetcodeId);
        }),
        finalize(() => {
          this.appStateService.updateIsBusySubject(false);
        })
      ).subscribe({
        next: (vetDoctorsVetVisitTypes) => {
          if (vetDoctorsVetVisitTypes) {
            for (const vetDoctorVetVisitType of vetDoctorsVetVisitTypes) {
              vetDoctorVetVisitType.vetDoctor = this.doctors.find(doctor => {
                return doctor.uuid === vetDoctorVetVisitType.vetDoctorUUID;
              });
              vetDoctorVetVisitType.vetineoVisitTypeBundle = {...this.vetineoVisitTypeBundle};
              // prevent circular JSON
              delete vetDoctorVetVisitType.vetineoVisitTypeBundle.vetDoctorVetVisitTypes;
            }
            this.modalController.dismiss(vetDoctorsVetVisitTypes);
          } else {
            this.commonUtils.presentAlertToast('Wystąpił błąd podczas dodawania typów wizyt');
          }
        },
        error: (error) => {
          this.commonUtils.presentAlertToast("Wystąpił błąd podczas dodawania typów wizyt");

        }
      })
    );
  }

  private doctorsHasDefinedDefaultMinutes(e: VetDoctor) {
    return e.defaultVisitMinutes;
  }

  getVetDoctorLabel(vetDoctor: VetDoctor) {
    return vetDoctor.defaultVisitMinutes ? vetDoctor.name : `${vetDoctor.name} (ustaw standardowy czas trwania wizyty)`;
  }

  checkAllDoctors() {
    this.doctorsSelected = this.doctorsAvailableToCheck;
  }

  uncheckAllDoctors() {
    this.doctorsSelected = [];
  }


  async setDefaultMinutesForDoctor(vetDoctor: VetDoctor, event) {
    vetDoctor.defaultVisitMinutes = event.detail.value;
    if (!this.vetDoctorsToSetDefaultMinutes.some(doctor => doctor.uuid === vetDoctor.uuid)) {
      this.vetDoctorsToSetDefaultMinutes.push(vetDoctor);
    }
    this.doctorsAvailableToCheck = this.getNotAssignedDoctors().filter(doctor => doctor.defaultVisitMinutes);
  }
}
